// @ts-strict-ignore
import { TFunction } from 'react-i18next'
import * as yup from 'yup'
import {
  BeneficiaryDetailsCurrency,
  BeneficiaryTransferDestination,
  PaymentMethod,
  USDBeneficiaryOptions,
} from 'types/beneficiaries'
import { NUMERIC_VALIDATION_PATTERN } from 'utils/yupSchemas/createNameSchema'

const CUBIX_ACCOUNT_NUMBER_EXACT_LENGTH = 4
export const DEFAULT_CBIT_BANK_CODE = 'CUESUS33'

export const LOCAL_USD_ACCOUNT_NUMBER_MIN_LENGTH = 4
export const LOCAL_USD_ACCOUNT_NUMBER_MAX_LENGTH = 17

type UsdAccountNumberValidatorProps = {
  value: string
  ctx: yup.TestContext<yup.AnyObject>
  t: TFunction<'translation', undefined>
  beneficiaryPaymentMethod: PaymentMethod
  isConfirmationField?: boolean
}

const usdAccountNumberTest = ({
  value,
  ctx,
  t,
  beneficiaryPaymentMethod,
  isConfirmationField = false,
}: UsdAccountNumberValidatorProps) => {
  if (!value && beneficiaryPaymentMethod !== USDBeneficiaryOptions.CUBIX) {
    ctx.schema.required()
    return ctx.createError({ message: t('accountNumberRequired') })
  }

  if (!NUMERIC_VALIDATION_PATTERN.test(value)) {
    return ctx.createError({
      message: t('onlyNumericValues'),
    })
  }

  if (
    value?.length < LOCAL_USD_ACCOUNT_NUMBER_MIN_LENGTH ||
    value?.length > LOCAL_USD_ACCOUNT_NUMBER_MAX_LENGTH
  ) {
    return ctx.createError({
      message: t('usdAccountNumberLengthError'),
    })
  }

  if (isConfirmationField && ctx?.parent?.usdAccountNumber !== value) {
    return ctx.createError({
      message: t('usdAccountAccountNumberMismatch'),
    })
  }

  if (!isConfirmationField && ctx?.parent?.usdAccountNumberConfirm !== value) {
    return ctx.createError({
      message: t('usdAccountAccountNumberMismatch'),
    })
  }

  return true
}

export const getUsdSpecificSchemaFields = (t: TFunction<'translation', undefined>) => {
  const usdAccountNumber = yup
    .string()
    .label(t('accountNumber'))
    .test({
      test(value, ctx) {
        return usdAccountNumberTest({
          value,
          ctx,
          beneficiaryPaymentMethod: ctx?.parent?.beneficiaryPaymentMethod,
          t,
        })
      },
    })

  const usdAccountNumberConfirm = yup
    .string()
    .label(t('confirmAccountNumber'))
    .test({
      test(value, ctx) {
        return usdAccountNumberTest({
          value,
          ctx,
          beneficiaryPaymentMethod: ctx?.parent?.beneficiaryPaymentMethod,
          t,
          isConfirmationField: true,
        })
      },
    })

  const abaRoutingNumberLabel = t('abaRoutingNumber')
  const abaRoutingNumber = yup.string().label(abaRoutingNumberLabel).length(9)

  const cbitLabel = t('cubixAccountNumberDigits')
  const cubixAccountNumberDigits = yup
    .string()
    .label(cbitLabel)
    .test({
      test(value, ctx) {
        // Make this required when the currency is USD and is CBIT beneficiary

        const isCubixAccountNumberDigitsLengthValid =
          value?.length === CUBIX_ACCOUNT_NUMBER_EXACT_LENGTH
        const isCubixAccountNumberDigitsFormatValid = !isNaN(Number(value))
        const beneficiaryPaymentMethod = ctx?.parent?.beneficiaryPaymentMethod

        if (!value && beneficiaryPaymentMethod === USDBeneficiaryOptions.CUBIX) {
          ctx.schema?.required()
          return ctx.createError({ message: t('cubixAccountNumberLastFourDigitsRequired') })
        }
        if (!isCubixAccountNumberDigitsLengthValid) {
          return ctx.createError({
            message: t('cubixAccountNumberDigitsFormatError'),
          })
        }

        if (!isCubixAccountNumberDigitsFormatValid) {
          return ctx.createError({
            message: t('cubixAccountNumberDigitsFormatError'),
          })
        }
        return true
      },
    })

  const cbitBankCode = yup.string().required()

  const accountTypeLabel = t('accountType')

  const accountType = yup
    .string()
    .label(accountTypeLabel)
    .test({
      test(value, ctx) {
        if (
          ctx?.parent?.currencyCode === BeneficiaryDetailsCurrency.USD &&
          ctx?.parent?.transferDestination === BeneficiaryTransferDestination.LOCAL
        ) {
          if (!value) {
            ctx.schema.required()
            return ctx.createError({ message: t('accountTypeRequired') })
          }
          return true
        }

        // Should not be required for other currencies
        ctx.schema.notRequired()
        return true
      },
    })

  return {
    usdAccountNumber,
    usdAccountNumberConfirm,
    abaRoutingNumber,
    cubixAccountNumberDigits,
    cbitBankCode,
    accountType,
  }
}
